// Wording of the hover card's political rows, kept apart from the component so it can be tested.

import type { City, SourceId } from '../core/schema.ts';
import type { HeldBasis } from './extensions.ts';
import { fmtCompact } from './format.ts';

/** A hex whose land is fully under the controller reads 255 in WorldSnapshot.controlFrac. */
const FULL_HOLD = 255;

/** What moved since the last card: the camera outranks the date, since the hex under the pointer changes with it. */
export type PickChange = 'camera moved' | 'date jump' | 'none';

/**
 * A date jump clears the card in the services, but the pointer still rests on the hex it named and
 * the view reports a hex only when it changes, so nothing would bring the card back. The pick to
 * put back: the last one, when the card is gone and only the date moved. A card cleared with the
 * camera moved names another hex now and stays cleared.
 */
export function restoredPick<T>(last: T | null, now: T | null, change: PickChange): T | null {
  return now === null && change === 'date jump' ? last : null;
}

/** Source ids of a UN General Assembly vote (docs/bake/borders.md, "De jure rule"): 'brd:wiki-unga-68-262'. */
const UNGA_VOTE_SOURCE = 'brd:wiki-unga-';

export interface ClaimBasis {
  /** Sources behind the hex's default-worldview owner. */
  sources: readonly SourceId[];
  /** Who holds the hex in the default worldview when that is not its owner; null = the owner itself. */
  heldBy: string | null;
}

/**
 * A leader's point of view can draw a hex as another state's than the default worldview does
 * (Crimea in the Russian view). The card must say so instead of naming one owner as fact.
 *
 *   UN General Assembly vote decided the owner   Shown as Russia in this view · recognised as Ukraine
 *   any other default owner                      Shown as China in this view · default map: Taiwan (administered separately)
 *
 * "recognised" claims a recognition, so it is worded only where such a vote exists.
 */
export function shownAs(povOwner: string | null, defaultOwner: string | null, basis: ClaimBasis): string | null {
  if (!povOwner || !defaultOwner) {
    return null;
  }

  const shown = `Shown as ${povOwner} in this view`;
  if (basis.sources.some((id) => id.startsWith(UNGA_VOTE_SOURCE))) {
    return `${shown} · recognised as ${defaultOwner}`;
  }

  const apart = basis.heldBy === null ? ' (administered separately)' : '';
  return `${shown} · default map: ${defaultOwner}${apart}`;
}

/** What a hex with no owner is called: water is not "unclaimed", it is not land. */
const WATER_NAME: Record<string, string> = { ocean: 'Open water', coast: 'Open water', lake: 'Lake' };
const UNCLAIMED_LAND = 'Unclaimed';
const NO_TERRAIN = 'No owner recorded';

export function unownedName(terrain: string | null): string {
  if (!terrain) {
    return NO_TERRAIN;
  }

  return WATER_NAME[terrain.toLowerCase()] ?? UNCLAIMED_LAND;
}

/**
 * The city the map draws a banner for on a hex that holds several: a capital first, then the most
 * populous (the order of view/political/labels.ts, so the card and the banner agree).
 */
export function topCity(cities: readonly City[]): City | undefined {
  return cities.reduce<City | undefined>((best, c) => {
    if (!best) {
      return c;
    }
    if (best.capital !== c.capital) {
      return c.capital ? c : best;
    }
    return c.pop > best.pop ? c : best;
  }, undefined);
}

/** 'city of Israel' for a city whose country is not the one that owns its hex, else null. */
export function cityCountryNote(cityCountry: string, hexOwner: string | null): string | null {
  return cityCountry === hexOwner ? null : `city of ${cityCountry}`;
}

const HELD_IN_FULL = 'Controlled by';
const HELD_IN_PART = 'Part of the area held by';
const HELD_UNKNOWN = 'Control';
const EXTENT_ASSUMED = 'extent not measured';
const HELD_OF_HEX = 'of this hex held by';
const PERCENT = 100;

/** A recorded share rounds to a whole percent, kept off 0% and 100%: "<1%" and ">99%". */
function sharePct(frac: number): string {
  const pct = Math.round((frac / FULL_HOLD) * PERCENT);
  if (pct < 1) {
    return '<1%';
  }
  return pct > PERCENT - 1 ? `>${PERCENT - 1}%` : `${pct}%`;
}

/**
 * How the controller row opens. A fraction between 0 and 255 from a source's measure reads as its
 * share ("2% of this hex held by": a city on a hex 5/255 held is not "partly occupied"). A partly
 * held admin-1 unit's share is unknown and drawn as half (world/control.ts PARTIAL_FRAC), so it is
 * worded as "part of the area", never as a percent. Where nobody observed who held the hex there is
 * no holder to name: "Control unknown · no observation until 2022-04-03".
 */
export function heldLead(frac: number | null, basis: HeldBasis | null): string {
  if (basis === 'unknown') {
    return HELD_UNKNOWN;
  }
  if (frac === null || frac <= 0 || frac >= FULL_HOLD) {
    return HELD_IN_FULL;
  }
  if (basis === 'recorded') {
    return `${sharePct(frac)} ${HELD_OF_HEX}`;
  }
  return HELD_IN_PART;
}

/** A partly held unit's extent is the map's convention, not a measurement: say so. */
export function heldNote(basis: HeldBasis | null): string | null {
  return basis === 'convention' ? EXTENT_ASSUMED : null;
}

const PART_SEP = ' · ';
const CLAIMED_BY = 'claimed by ';

/** HexDetail.control without its "claimed by X" part (the claim gets its own row), or null. */
export function heldLabel(control: string | null): string | null {
  if (!control) {
    return null;
  }
  const kept = control.split(PART_SEP).filter((part) => !part.startsWith(CLAIMED_BY));
  return kept.length > 0 ? kept.join(PART_SEP) : null;
}

const NO_POP_AT_DATE = 'no population figure at this date';
const UNDATED = 'undated';

/** The city's population, or null before its first datum (pop 0), which is no figure and not "0.0". */
export function popFigure(pop: number): string | null {
  return pop > 0 ? fmtCompact(pop) : null;
}

/** What to say beside the population: no figure at this date, or a figure without a year. */
export function popNote(pop: number, popYear: number): string | null {
  if (pop <= 0) {
    return NO_POP_AT_DATE;
  }
  return popYear === 0 ? UNDATED : null;
}
